import type { ReactNode } from 'react'
import { Autocomplete, Checkbox, MenuItem, TextField } from '@mui/material'
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank'
import CheckBoxIcon from '@mui/icons-material/CheckBox'

/**
 * Drop-in searchable replacement for MUI Select. Built on Autocomplete so
 * users can type to filter — useful for long user lists, asset pickers,
 * checklist multi-pickers, etc.
 *
 * Single:
 *   <SearchableSelect
 *     value={userId}
 *     onChange={setUserId}
 *     options={users}
 *     getValue={(u) => u.id}
 *     getLabel={(u) => u.name}
 *     renderOption={(u) => <UserRow u={u} />}
 *     placeholder="Select a person"
 *   />
 *
 * Multiple:
 *   <SearchableSelect
 *     multiple
 *     value={checklistIds}
 *     onChange={setChecklistIds}
 *     options={checklists}
 *     getValue={(c) => c.id}
 *     getLabel={(c) => c.name}
 *   />
 *
 * The custom `renderOption` only changes the row content — Autocomplete
 * still handles selection, keyboard nav, and (for multi) the leading
 * checkbox.
 */

type SingleProps<T> = {
  multiple?: false
  value: string
  onChange: (next: string) => void
}
type MultiProps<T> = {
  multiple: true
  value: string[]
  onChange: (next: string[]) => void
}

type CommonProps<T> = {
  options: T[]
  getValue: (opt: T) => string
  getLabel: (opt: T) => string
  renderOption?: (opt: T) => ReactNode
  placeholder?: string
  disabled?: boolean
  loading?: boolean
  size?: 'small' | 'medium'
  fullWidth?: boolean
  /** Allow free typing not in options (rarely needed). */
  freeSolo?: boolean
}

type Props<T> = CommonProps<T> & (SingleProps<T> | MultiProps<T>)

export function SearchableSelect<T>(props: Props<T>) {
  const {
    options, getValue, getLabel, renderOption,
    placeholder, disabled, loading, size = 'small', fullWidth = true,
  } = props

  const optionByValue = new Map(options.map((o) => [getValue(o), o]))

  // MUI's Autocomplete pins the popper width to the trigger via an
  // inline `style.width`. For our often-narrow triggers (Site, Type,
  // …) that truncates option labels like "AHMEDABAD - ICONIC SHYAMAL
  // (ADITYA BIRLA…)" badly. The popper modifier below overrides that
  // inline width to `auto` and instead sets `minWidth` to the trigger
  // width — the popper now grows to fit the widest option, capped at
  // ~520px so very long labels still ellipsize gracefully.
  const popperSlotProps: any = {
    popper: {
      sx: {
        '& .MuiAutocomplete-paper': {
          width: 'auto',
          maxWidth: 'min(520px, 92vw)',
        },
        '& .MuiAutocomplete-listbox': {
          maxWidth: 'min(520px, 92vw)',
        },
        '& .MuiAutocomplete-option': {
          // Long labels get an ellipsis instead of being clipped /
          // wrapped chaotically. Hovering still shows the full text
          // via the title attribute we attach in renderOption below.
          whiteSpace: 'nowrap',
          overflow: 'hidden',
          textOverflow: 'ellipsis',
          display: 'block',
        },
      },
      modifiers: [{
        name: 'overrideAnchorWidth',
        enabled: true,
        phase: 'beforeWrite',
        fn({ state }: any) {
          const ref = state.elements.reference
          if (ref?.offsetWidth) {
            // Drop the inline width MUI applied so the paper can
            // expand; require the popper to be at least as wide as
            // the trigger so the input doesn't visually shrink.
            state.styles.popper.width = 'auto'
            state.styles.popper.minWidth = `${ref.offsetWidth}px`
          }
        },
      }],
    },
  }

  if (props.multiple) {
    const selected = props.value
      .map((v) => optionByValue.get(v))
      .filter((o): o is T => o !== undefined)
    return (
      <Autocomplete
        multiple
        size={size}
        fullWidth={fullWidth}
        disabled={disabled}
        loading={loading}
        options={options}
        value={selected}
        disableCloseOnSelect
        slotProps={popperSlotProps}
        getOptionLabel={getLabel}
        isOptionEqualToValue={(a, b) => getValue(a) === getValue(b)}
        onChange={(_, next) => props.onChange(next.map(getValue))}
        renderOption={(rprops, option, { selected: sel }) => {
          const { key, ...rest } = rprops as { key: React.Key } & Record<string, unknown>
          const label = getLabel(option)
          return (
            <MenuItem key={key} {...rest as any} dense title={label}>
              <Checkbox
                size="small"
                icon={<CheckBoxOutlineBlankIcon fontSize="small" />}
                checkedIcon={<CheckBoxIcon fontSize="small" />}
                checked={sel}
                sx={{ mr: 1, p: 0.25 }}
              />
              {renderOption ? renderOption(option) : label}
            </MenuItem>
          )
        }}
        renderInput={(params) => (
          <TextField {...params} placeholder={selected.length === 0 ? placeholder : ''} />
        )}
      />
    )
  }

  const single = optionByValue.get(props.value) ?? null
  return (
    <Autocomplete
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      loading={loading}
      options={options}
      value={single}
      slotProps={popperSlotProps}
      getOptionLabel={getLabel}
      isOptionEqualToValue={(a, b) => getValue(a) === getValue(b)}
      onChange={(_, next) => props.onChange(next ? getValue(next) : '')}
      renderOption={(rprops, option) => {
        const { key, ...rest } = rprops as { key: React.Key } & Record<string, unknown>
        const label = getLabel(option)
        return (
          <MenuItem key={key} {...rest as any} dense title={label}>
            {renderOption ? renderOption(option) : label}
          </MenuItem>
        )
      }}
      renderInput={(params) => (
        <TextField {...params} placeholder={placeholder} />
      )}
    />
  )
}
